import SettingsConnection from 'sharedreact/connections/SettingsConnection';
import SettingsConstants from 'sharedreact/constants/SettingsConstants';
import type ErrorData from 'sharedreact/data/ErrorData';
import Settings from 'sharedreact/data/Settings';
import WarningsSettings from 'sharedreact/data/WarningsSettings';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import LanguageSupport from 'sharedreact/utils/LanguageSupport';

// private functions
function fetchSuccess (settings: Settings, userSettingsLoaded: boolean): void {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.GET_SUCCESS,
    item: settings,
    userSettingsLoaded: userSettingsLoaded,
    logLevel: 'debug'
  });
}

function fetchFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.GET_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function fetchFromServerSuccess (currentSettings: Settings, serverValues?: object): void {
  let settings = currentSettings;
  if (serverValues != null) {
    settings = currentSettings.mergeSettings(serverValues);
  }

  LanguageSupport.setLanguage(settings.language, () =>
    fetchSuccess(settings, true)
  );
}

function saveSuccess (settings: Settings) {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.SAVE_SUCCESS,
    item: settings,
    logLevel: 'info'
  });
}

function saveFailure (settings: Settings, error: ErrorData) {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.SAVE_FAIL,
    item: settings,
    error: error,
    logLevel: 'error'
  });
}

let _defaultSettings: Settings | undefined = undefined;

function _createDefaultSettings (defaultSettings?: Settings): Settings {
  if (_defaultSettings == null) {
    if (defaultSettings != null) {
      _defaultSettings = defaultSettings;
    } else {
      const browserLanguage = LanguageSupport.getBrowserLanguage(Settings.LanguageOptions);
      // set the language:
      // - from the browser (if available)
      // - en (fallback)
      _defaultSettings = new Settings();

      _defaultSettings =_defaultSettings
        .set('language', browserLanguage || 'en')
        .set('units', _defaultSettings.units
          .set('dimension', 'mm')
          .set('weight', 'g')
          .set('resolution', 'ppi')
          .set('ruling', 'lpi')
          .set('decimals', 3)
        );
    }
  }
  return _defaultSettings;
}

// exported functions
function setSettings (settings: Settings) {
  LanguageSupport.setLanguage(settings.language, () => fetchSuccess(settings, false));
}

function setDefaultSettings (defaultSettings: Settings | undefined = undefined, shouldFetchUserSettings: boolean = true) {
  if (!shouldFetchUserSettings) {
    AppDispatcher.dispatch({
      actionType: SettingsConstants.DONT_FETCH_USER_SETTINGS,
      logLevel: 'trace'
    });
  }

  const settings = _createDefaultSettings(defaultSettings);

  LanguageSupport.setLanguage(settings.language, () => fetchSuccess(settings, !shouldFetchUserSettings));
}

function fetchFromServer (settings?: Settings) {
  SettingsConnection.fetch(
    values => fetchFromServerSuccess(settings != null ? settings : _createDefaultSettings(), values),
    fetchFailure
  );
}

function save (settings: Settings): void {
  SettingsConnection.save(
    settings,
    saveSuccess,
    saveFailure
  );
}

function _setValue (name: string, value: string | number | boolean | WarningsSettings | null): void {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.SET_VALUE,
    name: name,
    value: value,
    logLevel: 'trace'
  });
}

function setValue (name: string, value: string | number | boolean | WarningsSettings | null) {
  if (name === 'language') {
    if (!(typeof value === 'string')) {
      throw new Error('IllegalArgumentException: value is not of type string');
    }
    
    AppDispatcher.dispatch({
      actionType: SettingsConstants.IS_SETTING_LANGUAGE,
      value: value,
      logLevel: 'trace'
    });

    LanguageSupport.setLanguage(value, () => _setValue(name, value));
  } else {
    _setValue(name, value);
  }
}

function setUnitsValue (name: string, value: string) {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.SET_UNITS_VALUE,
    name: name,
    value: value,
    logLevel: 'trace'
  });
}

function toggle () {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.TOGGLE,
    logLevel: 'trace'
  });
}

function hide () {
  AppDispatcher.dispatch({
    actionType: SettingsConstants.HIDE,
    logLevel: 'trace'
  });
}

export default {
  setSettings,
  setDefaultSettings,
  fetchFromServer,
  save,
  setValue,
  setUnitsValue,
  toggle,
  hide
};
